import axios from 'axios'
import { pathToRegexp } from 'path-to-regexp'
import { v4 as uuid } from 'uuid'
import history from 'history.js'
import { getCurrentTheme } from 'app/hooks/useCurrentTheme'
import { LOGIN } from 'app/ducks/auth/types'
import * as routes from 'app/views/routes'

export const ANALYTICS_EVENT = {
  FIRST_VISIT: 'first_visit',
  SESSION_START: 'session_start',
  PAGE_VIEW: 'page_view',
  USER_ENGAGEMENT: 'user_engagement',
  CLICK: 'click',
  SEARCH: 'search',
  FILE_DOWNLOAD: 'file_download',
  FORM_SUBMIT: 'form_submit',
  LOGIN: 'login',
  LOGOUT: 'logout',
  SIGN_UP: 'sign_up',
  APP_INSTALL: 'app_install',
  EXCEPTION: 'exception',
}

const CLIENT_ID_KEY = 'hall:analytics:client-id'
const SESSION_KEY = 'hall:analytics:session'

// Same inactivity window Google Analytics uses to decide a new session started
const SESSION_TIMEOUT = 30 * 60 * 1000
const FLUSH_INTERVAL = 15 * 1000
const MAX_QUEUE_LENGTH = 50
const REQUEST_TIMEOUT = 10 * 1000

const collectUrl = () =>
  `${process.env.REACT_APP_BACKEND_URL}/analytics/api/collect`

const readStorage = (key) => {
  try {
    return window.localStorage.getItem(key)
  } catch (error) {
    return null
  }
}

const writeStorage = (key, value) => {
  try {
    window.localStorage.setItem(key, value)
  } catch (error) {
    // Storage can be unavailable (private mode, quota), tracking must never break the app
  }
}

const pagePathPatterns = new Map()

/**
 * Resolves the route a path belongs to, e.g. `/user/details/:id` for
 * `/user/details/138`, so the events of every page can be correlated regardless of the
 * attributes on the url. Falls back to the raw path when no route matches.
 */
const getPagePathPattern = (pagePath) => {
  if (pagePathPatterns.has(pagePath)) return pagePathPatterns.get(pagePath)

  let pattern = pagePath
  try {
    const route = Object.values(routes).find(
      ({ path } = {}) =>
        typeof path === 'string' && pathToRegexp(path).test(pagePath)
    )
    pattern = route?.path || pagePath
  } catch (error) {
    pattern = pagePath
  }

  pagePathPatterns.set(pagePath, pattern)

  return pattern
}

const getDeviceType = () => {
  const userAgent = window.navigator.userAgent || ''
  if (/iPad|Tablet/i.test(userAgent)) return 'tablet'
  if (/Mobi|Android|iPhone/i.test(userAgent)) return 'mobile'
  return 'desktop'
}

const getPlatform = () => {
  if (window.location.pathname.startsWith('/totem')) return 'totem'
  const isStandalone =
    window.navigator.standalone ||
    window.matchMedia?.('(display-mode: standalone)')?.matches
  return isStandalone ? 'pwa' : 'web'
}

/**
 * Collects the application usage events on the application database, playing the role
 * Google Analytics would play. Events are queued and sent in batches to
 * `analytics/collect`, which accepts anonymous requests, so the unauthenticated pages
 * are tracked as well.
 */
class Analytics {
  static _queue = []
  static _flushTimeoutId = null
  static _initialized = false
  static _isFirstVisit = false
  static _currentPagePath = null
  static _currentPageStartedAt = null
  static _client = axios.create()

  static get clientId() {
    let clientId = readStorage(CLIENT_ID_KEY)
    if (!clientId) {
      clientId = uuid()
      writeStorage(CLIENT_ID_KEY, clientId)
    }
    return clientId
  }

  /**
   * Returns the current session key, starting a new session whenever the previous one
   * timed out.
   *
   * @returns {{ key: string, isNew: boolean }}
   */
  static get session() {
    const now = Date.now()
    let session = null

    try {
      session = JSON.parse(readStorage(SESSION_KEY))
    } catch (error) {
      session = null
    }

    const isExpired =
      !session?.key || !(now - (session.lastEventAt || 0) < SESSION_TIMEOUT)

    if (isExpired) session = { key: uuid(), isNew: true }
    else session.isNew = false

    writeStorage(SESSION_KEY, JSON.stringify({ ...session, lastEventAt: now }))

    return session
  }

  static get context() {
    return {
      platform: getPlatform(),
      device_type: getDeviceType(),
      user_agent: window.navigator.userAgent,
      language: window.navigator.language,
      screen_resolution: `${window.screen?.width}x${window.screen?.height}`,
      app_version: process.env.REACT_APP_VERSION,
      theme: getCurrentTheme(),
      referrer: window.document.referrer,
      landing_page: window.location.pathname,
      is_first_visit: this._isFirstVisit,
    }
  }

  /**
   * Starts collecting events: session/first visit events, a page view for the current
   * location and a page view for every subsequent navigation.
   */
  static init() {
    if (this._initialized) return
    this._initialized = true

    this._isFirstVisit = !readStorage(CLIENT_ID_KEY)
    const { isNew: isNewSession } = this.session

    if (this._isFirstVisit) this.logEvent(ANALYTICS_EVENT.FIRST_VISIT)
    if (isNewSession) this.logEvent(ANALYTICS_EVENT.SESSION_START)

    this.trackPageView()
    history.listen(() => this.trackPageView())

    window.addEventListener('pagehide', () => this._onPageHide())
    window.addEventListener('visibilitychange', () => {
      if (window.document.visibilityState === 'hidden') this._onPageHide()
    })
  }

  static trackPageView() {
    const pagePath = window.location.pathname

    // React re-renders and replaced query strings must not duplicate the page view
    if (pagePath === this._currentPagePath) return

    this._logEngagement()
    this._currentPagePath = pagePath
    this._currentPageStartedAt = Date.now()

    this.logEvent(ANALYTICS_EVENT.PAGE_VIEW)
  }

  /**
   * Queues an event, mirroring `Firebase.logEvent`'s signature.
   *
   * @param {string} eventName Snake cased event name
   * @param {object} eventParams Flat object of primitive values
   */
  static logEvent(eventName, eventParams = {}) {
    if (!eventName) return

    this._queue.push({
      name: eventName,
      occurred_date: new Date().toISOString(),
      page_path: window.location.pathname,
      page_path_pattern: getPagePathPattern(window.location.pathname),
      page_title: window.document.title,
      page_location: window.location.href,
      page_referrer: window.document.referrer,
      params: eventParams,
    })

    if (this._queue.length >= MAX_QUEUE_LENGTH) this.flush()
    else this._scheduleFlush()
  }

  static async flush() {
    this._clearScheduledFlush()

    if (!this._queue.length) return

    const events = this._queue
    this._queue = []

    try {
      await this._client.post(collectUrl(), this._buildPayload(events), {
        timeout: REQUEST_TIMEOUT,
        headers: this._headers,
      })
    } catch (error) {
      console.warn('Unable to collect analytics events', error)
    }
  }

  static get _headers() {
    // Read from the persisted auth state instead of the store, which would depend on
    // this module through the auth actions
    let token = null
    try {
      token = JSON.parse(readStorage(LOGIN))?.token
    } catch (error) {
      token = null
    }

    const headers = { 'Content-Type': 'application/json' }
    if (token) headers['authorization'] = `Token ${token}`

    return headers
  }

  static _buildPayload(events) {
    return {
      client_id: this.clientId,
      session_key: this.session.key,
      context: this.context,
      events,
    }
  }

  static _logEngagement() {
    if (!this._currentPageStartedAt) return

    const engagementTime = Date.now() - this._currentPageStartedAt
    this._currentPageStartedAt = null

    this._queue.push({
      name: ANALYTICS_EVENT.USER_ENGAGEMENT,
      occurred_date: new Date().toISOString(),
      page_path: this._currentPagePath,
      page_path_pattern: getPagePathPattern(this._currentPagePath),
      page_title: window.document.title,
      page_location: window.location.href,
      engagement_time_msec: engagementTime,
    })
  }

  static _onPageHide() {
    this._logEngagement()
    this._clearScheduledFlush()

    if (!this._queue.length) return

    const events = this._queue
    this._queue = []

    // A regular request is aborted while the page unloads, so the queue is beaconed
    const payload = JSON.stringify(this._buildPayload(events))
    const wasSent = window.navigator.sendBeacon?.(
      collectUrl(),
      new Blob([payload], { type: 'application/json' })
    )

    if (!wasSent) this._queue = events
  }

  static _scheduleFlush() {
    if (this._flushTimeoutId) return
    this._flushTimeoutId = window.setTimeout(() => this.flush(), FLUSH_INTERVAL)
  }

  static _clearScheduledFlush() {
    if (!this._flushTimeoutId) return
    window.clearTimeout(this._flushTimeoutId)
    this._flushTimeoutId = null
  }
}

export default Analytics
